"use client";

import Link from "next/link";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  check_auth,
  createFullSignupOrder,
  getPaidCourses,
  getStudentDetail,
  verifyFullSignupPayment,
} from "@/api";

const getCourseId = (course) => course?.id || course?._id || "";
const TERMS_HREF = "/terms-and-conditions";
const PRIVACY_HREF = "/privacy-policy";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (typeof window !== "undefined" && window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
const normalizePhone = (value) => String(value || "").replace(/\D/g, "");

function FullRegisterPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone_number: "",
    password: "",
    confirm_password: "",
    course_id: "",
    accepted_legal: false,
  });

  useEffect(() => {
    const init = async () => {
      const selectedCourse = searchParams.get("course_id");
      if (selectedCourse) {
        setForm((prev) => ({ ...prev, course_id: selectedCourse }));
      }

      try {
        const session = await check_auth();
        if (session) {
          const student = await getStudentDetail();
          setIsLoggedIn(true);
          setUser(student);
          setForm((prev) => ({
            ...prev,
            name: student?.name || prev.name,
            email: student?.email || prev.email,
            phone_number: student?.phone_number || prev.phone_number,
          }));
        }
      } catch {
        // ignore
      } finally {
        setCheckingAuth(false);
      }

      try {
        const paidCourses = await getPaidCourses();
        setCourses(Array.isArray(paidCourses) ? paidCourses : []);
      } catch {
        setStatus({
          type: "error",
          message: "Could not load paid courses right now.",
        });
      } finally {
        setLoadingCourses(false);
      }
    };

    init();
  }, [searchParams]);

  const validationError = useMemo(() => {
    if (!form.course_id) return "Please select a course.";
    if (!form.accepted_legal) {
      return "Please accept the Terms & Conditions and Privacy Policy.";
    }
    if (isLoggedIn) return "";
    if (String(form.name || "").trim().length < 3) {
      return "Name must be at least 3 characters.";
    }
    if (!validateEmail(form.email)) {
      return "Enter a valid email address.";
    }
    if (normalizePhone(form.phone_number).length !== 10) {
      return "Phone number must be 10 digits.";
    }
    if (String(form.password || "").length < 8) {
      return "Password must be at least 8 characters.";
    }
    if (form.password !== form.confirm_password) {
      return "Password and confirm password must match.";
    }
    return "";
  }, [form, isLoggedIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (validationError) {
      setStatus({ type: "error", message: validationError });
      return;
    }

    setSubmitting(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Could not load Razorpay checkout.");
      }

      const orderPayload = isLoggedIn
        ? {
            name: user?.name || form.name,
            email: user?.email || form.email,
            phone_number: user?.phone_number || form.phone_number,
            password: "",
            course_id: form.course_id,
          }
        : {
            name: form.name.trim(),
            email: form.email.trim(),
            phone_number: normalizePhone(form.phone_number),
            password: form.password,
            course_id: form.course_id,
          };

      const order = await createFullSignupOrder(orderPayload);
      const selectedCourse = courses.find(
        (course) => getCourseId(course) === form.course_id
      );

      const paymentResult = await new Promise((resolve, reject) => {
        const razorpay = new window.Razorpay({
          key: order?.key_id,
          amount: order?.amount,
          currency: order?.currency || "INR",
          name: "SOFAR",
          description:
            selectedCourse?.title || order?.course?.title || "Course Enrollment",
          order_id: order?.order_id,
          prefill: order?.prefill,
          theme: {
            color: "#0D6FEA",
          },
          modal: {
            ondismiss: () => reject(new Error("Payment was cancelled.")),
          },
          handler: resolve,
        });

        razorpay.open();
      });

      setIsVerifyingPayment(true);
      const verification = await verifyFullSignupPayment(paymentResult);
      router.replace(verification?.redirect_to || "/dashboard");
    } catch (error) {
      const code = error?.response?.data?.code;
      if (code === "login_required") {
        const next = `/register/full?course_id=${encodeURIComponent(form.course_id)}`;
        setStatus({
          type: "error",
          message:
            error?.response?.data?.error ||
            "This account already exists. Please login to continue buying courses.",
        });
        router.push(`/login?next=${encodeURIComponent(next)}`);
        return;
      }

      setStatus({
        type: "error",
        message:
          error?.response?.data?.error ||
          error?.message ||
          "Could not complete your payment.",
      });
    } finally {
      setSubmitting(false);
      setIsVerifyingPayment(false);
    }
  };

  const isBusy = submitting || isVerifyingPayment;

  if (checkingAuth) return null;

  return (
    <section className="min-h-screen bg-[radial-gradient(circle_at_top_left,#eef7ff,transparent_38%),linear-gradient(160deg,#ffffff_0%,#f8fbff_55%,#edf4ff_100%)] px-4 py-10">
      {isVerifyingPayment ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1d34]/65 backdrop-blur-sm">
          <div className="rounded-[28px] bg-white px-8 py-7 text-center shadow-[0_24px_80px_rgba(0,0,0,0.18)]">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#d7e7ff] border-t-[#0d6fea]" />
            <p className="mt-4 text-lg font-semibold text-[#1a1a1a]">
              Verifying payment
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Please do not refresh or go back.
            </p>
          </div>
        </div>
      ) : null}

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-[28px] border border-[#d8e7ff] bg-white p-8 shadow-[0_20px_60px_rgba(10,45,90,0.08)]">
          <p className="text-sm font-medium uppercase tracking-[0.18em] text-[#0d6fea]">
            Full Course Signup
          </p>
          <h1 className="mt-3 text-3xl font-semibold text-[#1a1a1a]">
            Buy your course and create your account in one step
          </h1>
          <p className="mt-4 text-base leading-7 text-gray-600">
            Choose a full course, create your student account, complete payment
            via Razorpay, and you will be redirected to your dashboard with
            access enabled.
          </p>

          {isLoggedIn ? (
            <div className="mt-6 rounded-3xl border border-[#d8e7ff] bg-[#f7fbff] p-5">
              <p className="text-sm font-medium text-[#1a1a1a]">
                Logged in as {user?.name || user?.email}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                You can buy another course using your existing account.
              </p>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
            <input
              type="text"
              placeholder="Full name"
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea] disabled:bg-gray-50"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              disabled={isLoggedIn || isBusy}
              required
            />
            <input
              type="email"
              placeholder="Email address"
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea] disabled:bg-gray-50"
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
              disabled={isLoggedIn || isBusy}
              required
            />
            <input
              type="tel"
              placeholder="Phone number"
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea] disabled:bg-gray-50"
              value={form.phone_number}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, phone_number: e.target.value }))
              }
              disabled={isLoggedIn || isBusy}
              required
            />
            {!isLoggedIn ? (
              <>
                <input
                  type="password"
                  placeholder="Password"
                  minLength={8}
                  className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
                  value={form.password}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, password: e.target.value }))
                  }
                  disabled={isBusy}
                  required
                />
                <input
                  type="password"
                  placeholder="Confirm password"
                  minLength={8}
                  className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
                  value={form.confirm_password}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      confirm_password: e.target.value,
                    }))
                  }
                  disabled={isBusy}
                  required
                />
              </>
            ) : null}
            <select
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea] bg-white"
              value={form.course_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, course_id: e.target.value }))
              }
              required
              disabled={loadingCourses || isBusy}
            >
              <option value="">
                {loadingCourses ? "Loading courses..." : "Select a full course"}
              </option>
              {courses.map((course) => (
                <option key={getCourseId(course)} value={getCourseId(course)}>
                  {course?.title}
                </option>
              ))}
            </select>

            <label className="flex items-start gap-3 rounded-2xl border border-[#d8e7ff] bg-[#f7fbff] px-4 py-4 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.accepted_legal}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    accepted_legal: e.target.checked,
                  }))
                }
                disabled={isBusy}
                className="mt-0.5"
              />
              <span>
                I agree to the{" "}
                <Link href={TERMS_HREF} target="_blank" className="text-[#0d6fea] underline">
                  Terms & Conditions
                </Link>{" "}
                and{" "}
                <Link href={PRIVACY_HREF} target="_blank" className="text-[#0d6fea] underline">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            {status.message ? (
              <p
                className={`text-sm ${
                  status.type === "success" ? "text-green-600" : "text-red-600"
                }`}
              >
                {status.message}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={isBusy || loadingCourses}
              className="rounded-2xl bg-[#0d6fea] px-4 py-3 font-medium text-white disabled:opacity-60"
            >
              {isBusy ? "Processing..." : isLoggedIn ? "Buy Course" : "Pay & Create Account"}
            </button>
          </form>

          <div className="mt-5 rounded-2xl border border-[#d8e7ff] bg-[#f7fbff] px-4 py-4">
            <p className="text-sm font-medium text-[#1a1a1a]">
              Want to try a demo first?
            </p>
            <Link
              href="/register"
              className="mt-2 inline-flex text-sm font-medium text-[#0d6fea]"
            >
              Switch to demo signup
            </Link>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm">
            <Link href="/login" className="text-[#0d6fea]">
              Already have an account? Login
            </Link>
          </div>
        </div>

        <div className="rounded-[28px] bg-[#0a2f5e] p-8 text-white shadow-[0_28px_80px_rgba(8,36,72,0.22)]">
          <p className="text-sm uppercase tracking-[0.24em] text-blue-200">
            Available Full Courses
          </p>
          <div className="mt-6 grid gap-4">
            {loadingCourses ? (
              <div className="rounded-3xl border border-white/10 bg-white/10 p-5 text-sm text-blue-100">
                Loading paid courses...
              </div>
            ) : courses.length === 0 ? (
              <div className="rounded-3xl border border-white/10 bg-white/10 p-5 text-sm text-blue-100">
                No paid courses are available right now.
              </div>
            ) : (
              courses.map((course) => {
                const price = course?.is_discounted && course?.discount_price
                  ? course.discount_price
                  : course?.price;
                return (
                  <button
                    key={getCourseId(course)}
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        course_id: getCourseId(course),
                      }))
                    }
                    className={`rounded-3xl border p-5 text-left transition ${
                      form.course_id === getCourseId(course)
                        ? "border-white bg-white text-[#0a2f5e]"
                        : "border-white/15 bg-white/10 text-white hover:bg-white/15"
                    }`}
                  >
                    <p className="text-xl font-semibold">{course?.title}</p>
                    <p
                      className={`mt-2 text-sm leading-6 ${
                        form.course_id === getCourseId(course)
                          ? "text-[#375177]"
                          : "text-blue-100"
                      }`}
                    >
                      {course?.short_description || course?.description || "Full course access"}
                    </p>
                    <p
                      className={`mt-4 text-sm font-medium ${
                        form.course_id === getCourseId(course)
                          ? "text-[#0a2f5e]"
                          : "text-white"
                      }`}
                    >
                      INR {price || course?.price || "N/A"}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function FullRegisterPage() {
  return (
    <Suspense fallback={null}>
      <FullRegisterPageContent />
    </Suspense>
  );
}
